import { supabase } from '../../../services/supabase/client'
import type { Job, JobInsert, JobUpdate, JobWithDetails, JobFilters, JobStatus } from '../types/job'
import { createJobSchema, updateJobSchema } from '../types/schemas'
import { offlineStorageService } from '../../sync/services/offlineStorage'
import { buildSyncQueueItem } from '../../sync/services/syncQueue'
import { reportBackgroundSyncFailure } from '../../sync/services/syncErrorReporter'
import { withTimeout, ONLINE_REQUEST_TIMEOUT_MS } from '../../sync/services/withTimeout'
import { purgeJobStorage } from '../../../services/storage/storageCleanup'
import { v4 as uuidv4 } from 'uuid'
import {
  CURRENT_QUOTE_EMBED,
  applyQuoteFilter,
  currentQuoteEmbed,
} from '../../quotes/lib/quoteFilters'
import type { JobQuoteSummary } from '../../quotes/types'

/** A jobs row as the list/detail selects return it, embeds included. */
type JobRowWithEmbeds = Job & {
  client_organizations: { name: string } | null
  users: { first_name: string | null; last_name: string | null } | null
  inspection_nodes?: { count: number }[] | null
  current_quote: JobQuoteSummary | null
}

/** Single-job select: the job, its client/arborist names and its current quote. */
const JOB_DETAIL_SELECT = `*, client_organizations(name), users(first_name, last_name), ${CURRENT_QUOTE_EMBED}`

export class JobService {
  /**
   * Get all jobs with optional filters
   * OFFLINE-FIRST APPROACH: Load from IndexedDB immediately, sync in background
   * If IndexedDB is empty and online, wait for sync to complete
   */
  async getJobs(filters?: JobFilters): Promise<JobWithDetails[]> {
    // OFFLINE-FIRST: Always load from IndexedDB first for instant response
    const localJobs = await offlineStorageService.getJobs(filters)

    // If online, sync with Supabase
    if (navigator.onLine) {
      // If we have local data, sync in background (fire and forget)
      // If IndexedDB is empty, wait for sync to complete (fresh login scenario)
      if (localJobs.length > 0) {
        void this.syncJobsInBackground(filters)
        return localJobs
      } else {
        // No local data - wait for Supabase fetch
        const freshJobs = await this.fetchJobsFromSupabase(filters)
        return freshJobs
      }
    }

    return localJobs
  }

  /**
   * Build the jobs list query with filters applied. Count is included so
   * paginated callers know the total; `range` must come last because it
   * narrows the builder past the filter methods.
   */
  private buildJobsQuery(filters?: JobFilters, range?: [number, number]) {
    // The embed is built at runtime (008: `!inner` only under a quote filter),
    // so the row type is declared rather than inferred from the select string
    let query = supabase
      .from('jobs')
      .select<string, JobRowWithEmbeds>(
        `*, client_organizations(name), users(first_name, last_name), inspection_nodes(count), ${currentQuoteEmbed(filters?.quote_status)}`,
        { count: 'exact' }
      )
      .order('created_at', { ascending: false })

    if (filters?.quote_status) {
      query = applyQuoteFilter(query, filters.quote_status)
    }

    if (filters?.status) {
      if (Array.isArray(filters.status)) {
        query = query.in('status', filters.status)
      } else {
        query = query.eq('status', filters.status)
      }
    }

    if (filters?.assigned_arborist_id) {
      query = query.eq('arborist_id', filters.assigned_arborist_id)
    }

    if (filters?.show_unassigned) {
      query = query.is('arborist_id', null)
    }

    if (filters?.org_id) {
      query = query.eq('org_id', filters.org_id)
    }

    if (filters?.client_org_id) {
      query = query.eq('client_org_id', filters.client_org_id)
    }

    if (filters?.search_query) {
      query = query.or(
        `title.ilike.%${filters.search_query}%,site_address_line1.ilike.%${filters.search_query}%`
      )
    }

    if (filters?.date_from) {
      query = query.gte('created_at', filters.date_from)
    }

    if (filters?.date_to) {
      query = query.lte('created_at', filters.date_to)
    }

    return range ? query.range(range[0], range[1]) : query
  }

  /**
   * Fold the embedded `inspection_nodes(count)` aggregate into the flat
   * `node_count` the job list renders, and drop the embed so it never reaches
   * the IndexedDB cache. PostgREST returns the aggregate as a one-element
   * array (`[{ count: n }]`), and omits it entirely on some paths, so both
   * shapes collapse to 0.
   */
  private withNodeCounts(rows: unknown[]): JobWithDetails[] {
    return rows.map((row) => {
      const { inspection_nodes: nodes, ...job } = row as Record<string, unknown> & {
        inspection_nodes?: { count: number }[] | null
        current_quote?: JobQuoteSummary | null
      }
      return {
        ...job,
        // The to-one embed is already flat; pin the "no quote" shape to null
        // so cached rows and fresh rows compare alike (008 FR-021)
        current_quote: job.current_quote ?? null,
        node_count: nodes?.[0]?.count ?? 0,
      } as JobWithDetails
    })
  }

  /**
   * Fetch one page of jobs (REL-08). Online, this hits Supabase with
   * `.range()` and caches the page; offline, it pages through the IndexedDB
   * cache with the same ordering.
   */
  async getJobsPage(
    filters: JobFilters | undefined,
    page: number,
    pageSize: number
  ): Promise<{ jobs: JobWithDetails[]; total: number }> {
    if (!navigator.onLine) {
      const cached = await offlineStorageService.getJobs(filters)
      const sorted = [...cached].sort((a, b) =>
        (b.created_at ?? '').localeCompare(a.created_at ?? '')
      )
      return {
        jobs: sorted.slice(page * pageSize, (page + 1) * pageSize),
        total: sorted.length,
      }
    }

    const from = page * pageSize
    const { data, error, count } = await this.buildJobsQuery(filters, [from, from + pageSize - 1])
    if (error) throw error

    const jobs = this.withNodeCounts(data ?? [])

    if (jobs.length > 0) {
      await offlineStorageService.cacheJobs(jobs)
    }

    return { jobs, total: count ?? 0 }
  }

  /**
   * Per-status job counts for the dashboard summary (REL-08 companion —
   * totals can no longer be derived from a fully-loaded list).
   */
  async getJobStatusCounts(): Promise<{
    total: number
    draft: number
    in_progress: number
    review: number
    completed: number
  }> {
    if (!navigator.onLine) {
      const cached = await offlineStorageService.getJobs()
      return {
        total: cached.length,
        draft: cached.filter((job) => job.status === 'draft').length,
        in_progress: cached.filter((job) => job.status === 'in_progress').length,
        review: cached.filter((job) => job.status === 'review').length,
        completed: cached.filter((job) => job.status === 'completed').length,
      }
    }

    const countFor = async (status?: JobStatus) => {
      let query = supabase.from('jobs').select('id', { count: 'exact', head: true })
      if (status) query = query.eq('status', status)
      const { count, error } = await query
      if (error) throw error
      return count ?? 0
    }

    const [total, draft, inProgress, review, completed] = await Promise.all([
      countFor(),
      countFor('draft'),
      countFor('in_progress'),
      countFor('review'),
      countFor('completed'),
    ])

    return { total, draft, in_progress: inProgress, review, completed }
  }

  /**
   * Fetch jobs directly from Supabase and cache them
   */
  private async fetchJobsFromSupabase(filters?: JobFilters): Promise<JobWithDetails[]> {
    try {
      const { data, error } = await this.buildJobsQuery(filters)

      if (error) throw error

      // Update IndexedDB cache with fresh data
      if (data) {
        const jobs = this.withNodeCounts(data)
        await offlineStorageService.cacheJobs(jobs)
        return jobs
      }

      return []
    } catch (error) {
      console.error('Failed to fetch jobs from Supabase:', error)
      return []
    }
  }

  /**
   * Background sync for jobs
   * Updates IndexedDB cache with fresh data from Supabase
   */
  private async syncJobsInBackground(filters?: JobFilters): Promise<void> {
    try {
      const { data, error } = await this.buildJobsQuery(filters)

      if (error) throw error

      // Update IndexedDB cache with fresh data
      if (data) {
        await offlineStorageService.cacheJobs(this.withNodeCounts(data))
      }
    } catch (error) {
      reportBackgroundSyncFailure('Background job list refresh failed', error)
    }
  }

  /**
   * Get a single job by ID
   * OFFLINE-FIRST APPROACH: Load from IndexedDB immediately, sync in background
   * If IndexedDB doesn't have the job and online, wait for sync to complete
   */
  async getJob(id: string): Promise<JobWithDetails | null> {
    // OFFLINE-FIRST: Always load from IndexedDB first for instant response
    const localJob = await offlineStorageService.getJob(id)

    // If online, sync with Supabase
    if (navigator.onLine) {
      // If we have local data, sync in background (fire and forget)
      // If not found locally, wait for Supabase fetch
      if (localJob) {
        void this.syncJobInBackground(id)
        return localJob
      } else {
        // Not found locally - fetch from Supabase
        const freshJob = await this.fetchJobFromSupabase(id)
        return freshJob
      }
    }

    return localJob
  }

  /**
   * Fetch a single job directly from Supabase and cache it
   */
  private async fetchJobFromSupabase(id: string): Promise<JobWithDetails | null> {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select<string, JobRowWithEmbeds>(JOB_DETAIL_SELECT)
        .eq('id', id)
        .single()

      if (error) throw error
      if (!data) return null

      // Get node count
      const { count: nodeCount } = await supabase
        .from('inspection_nodes')
        .select('*', { count: 'exact', head: true })
        .eq('job_id', id)

      // Get photo count
      const { count: photoCount } = await supabase
        .from('photos')
        .select('*', { count: 'exact', head: true })
        .eq('job_id', id)

      const enrichedJob = {
        ...data,
        node_count: nodeCount || 0,
        photo_count: photoCount || 0,
      }

      // Update IndexedDB cache
      await offlineStorageService.saveJob(enrichedJob)

      return enrichedJob as JobWithDetails
    } catch (error) {
      console.error('Failed to fetch job from Supabase:', error)
      return null
    }
  }

  /**
   * Background sync for single job
   * Updates IndexedDB cache with fresh data from Supabase
   */
  private async syncJobInBackground(id: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select<string, JobRowWithEmbeds>(JOB_DETAIL_SELECT)
        .eq('id', id)
        .single()

      if (error) throw error
      if (!data) return

      // Get node count
      const { count: nodeCount } = await supabase
        .from('inspection_nodes')
        .select('*', { count: 'exact', head: true })
        .eq('job_id', id)

      // Get photo count
      const { count: photoCount } = await supabase
        .from('photos')
        .select('*', { count: 'exact', head: true })
        .eq('job_id', id)

      const enrichedJob = {
        ...data,
        node_count: nodeCount || 0,
        photo_count: photoCount || 0,
      }

      // Update IndexedDB cache
      await offlineStorageService.saveJob(enrichedJob)
    } catch (error) {
      reportBackgroundSyncFailure('Background job refresh failed', error)
    }
  }

  /**
   * Create a new job
   */
  async createJob(jobData: JobInsert): Promise<Job> {
    // Validate data
    console.log('JobService.createJob - Input data:', jobData)
    try {
      const validated = createJobSchema.parse(jobData)
      console.log('JobService.createJob - Validated data:', validated)
    } catch (error) {
      console.error('JobService.createJob - Validation error:', error)
      throw error
    }
    const validated = createJobSchema.parse(jobData)

    // One id for every path — online insert, offline record, queued replay.
    // If the online attempt times out AFTER the server accepted it, the
    // queued create collides on the primary key instead of making a twin;
    // and photos captured in the job get a real storage-key prefix.
    const jobId = uuidv4()

    try {
      // If online, create in Supabase — bounded, so a dead link that
      // navigator.onLine misreports (VPN, captive portal) falls through to
      // the offline store instead of hanging (see withTimeout invariant)
      if (navigator.onLine) {
        // job_number is filled by a BEFORE INSERT trigger; the generated
        // Insert type can't express that, so assert the narrower payload
        const { data, error } = await withTimeout(
          supabase
            .from('jobs')
            .insert({ ...validated, id: jobId } as JobInsert)
            .select()
            .single(),
          ONLINE_REQUEST_TIMEOUT_MS,
          'job create'
        )

        if (error) throw error
        return data
      }
    } catch (error) {
      console.error('Failed to create job online:', error)
    }

    // If offline or online creation failed, create locally under the SAME id
    const localJob: Job = {
      id: jobId,
      ...validated,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      completed_date: null,
      scheduled_date: null,
      delivered_date: null,
      site_latitude: null,
      site_longitude: null,
      description: validated.description || null,
      site_address_line2: null,
      site_address_city: null,
      site_address_state: null,
      site_address_postal_code: null,
      org_id: validated.org_id,
    } as Job

    // Store in IndexedDB and enqueue for sync in one transaction (REL-09).
    // The payload carries the client id so the queued insert is idempotent.
    await offlineStorageService.saveJobWithQueueItem(
      localJob,
      buildSyncQueueItem('create', 'job', jobId, { ...validated, id: jobId })
    )

    return localJob
  }

  /**
   * Update an existing job
   */
  async updateJob(id: string, updates: JobUpdate): Promise<Job> {
    // Validate data
    const validated = updateJobSchema.parse(updates)

    try {
      // If online, update in Supabase (bounded — see withTimeout invariant)
      if (navigator.onLine) {
        const { data, error } = await withTimeout(
          supabase.from('jobs').update(validated).eq('id', id).select().single(),
          ONLINE_REQUEST_TIMEOUT_MS,
          'job update'
        )

        if (error) throw error

        // Write through to the offline cache — offline-first reads (getJob)
        // would otherwise serve the stale row until a background sync runs
        try {
          const cached = await offlineStorageService.getJob(id)
          if (cached) {
            await offlineStorageService.saveJob({ ...cached, ...data })
          }
        } catch (cacheError) {
          console.warn('Job cache refresh after update failed:', cacheError)
        }

        return data
      }
    } catch (error) {
      console.error('Failed to update job online:', error)
    }

    // If offline or online update failed, update locally
    const existingJob = await offlineStorageService.getJob(id)
    if (!existingJob) {
      throw new Error('Job not found')
    }

    const updatedJob: Job = {
      ...existingJob,
      ...validated,
      updated_at: new Date().toISOString(),
    }

    // Store in IndexedDB and enqueue for sync in one transaction (REL-09)
    await offlineStorageService.saveJobWithQueueItem(
      updatedJob,
      buildSyncQueueItem('update', 'job', id, validated)
    )

    return updatedJob
  }

  /**
   * Delete a job
   */
  async deleteJob(id: string): Promise<void> {
    try {
      // If online, delete from Supabase (bounded — see withTimeout invariant)
      if (navigator.onLine) {
        // Storage first: every bucket policy resolves access through the job
        // row, so once the row is gone its photos, thumbnails and report PDFs
        // can never be removed. Best-effort — a storage failure must not
        // block the delete the user asked for.
        await purgeJobStorage(id)

        const { error } = await withTimeout(
          supabase.from('jobs').delete().eq('id', id),
          ONLINE_REQUEST_TIMEOUT_MS,
          'job delete'
        )

        if (error) throw error

        // Also delete from IndexedDB if stored locally
        await offlineStorageService.deleteJob(id)
        return
      }
    } catch (error) {
      console.error('Failed to delete job online:', error)
    }

    // If offline, delete locally and enqueue for sync in one transaction (REL-09)
    await offlineStorageService.deleteJobWithQueueItem(
      id,
      buildSyncQueueItem('delete', 'job', id, { id })
    )
  }

  /**
   * Mark job as complete
   */
  async completeJob(id: string): Promise<Job> {
    return this.updateJob(id, {
      status: 'completed',
      completed_date: new Date().toISOString(),
    })
  }
}

// Export singleton instance
export const jobService = new JobService()
